import { Group } from 'src/design/atoms/Group';
import { Loader } from 'src/design/atoms/Loader';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Title } from 'src/design/atoms/Title';
import { TextInput } from 'src/design/atoms/TextInput';
import React from 'react';
import { DeleteActionIcon } from 'src/design/molecules/DeleteActionIcon';
import { CreateButton } from 'src/design/atoms/CreateButton';
import { SaveButton } from 'src/design/atoms/SaveButton';
import { Header } from 'src/design/organisms/Header';

import { Hyperlink, HyperlinkRequest } from 'src/api/FiretrolApiClient';
import { useJobHyperlinks } from 'src/api/queries/jobs/useJobHyperlinks';
import {
  useRemoveHyperlink,
  useSaveHyperlink,
} from 'src/api/queries/jobs/useHyperlinkMutations';
import { useJobs } from 'src/hooks/useJobs';

export const Hyperlinks: React.FC = () => {
  const { selectedJob } = useJobs();
  const { data, isLoading } = useJobHyperlinks(selectedJob?.id);
  const hyperlinks = data ?? [];

  if (!selectedJob) return null;

  return (
    <>
      <Header
        breadcrumbProps={{
          items: [
            { text: 'Jobs', href: '/jobs' },
            {
              text: `${selectedJob.name} - ${selectedJob.jobNumber}`,
              href: `/jobs/${selectedJob.id}/hyperlinks`,
            },
            { text: 'Project Hyperlinks', href: '' },
          ],
        }}
        title="Hyperlinks"
        helpText="Manage hyperlinks for this job. Hyperlinks are displayed on the job summary page and in the Dailies module."
      />
      {isLoading ? (
        <Group justify="center" h={300}>
          <Loader />
        </Group>
      ) : (
        <Stack>
          <>
            {hyperlinks.map((h) => (
              <HyperlinkRow
                key={h.id}
                jobId={selectedJob.id}
                initialValues={h}
              />
            ))}
            <Title order={4}>Add New Hyperlink</Title>
            <HyperlinkRow jobId={selectedJob.id} />
          </>
        </Stack>
      )}
    </>
  );
};

interface HyperlinkRowProps {
  jobId: number;
  initialValues?: Hyperlink;
}

function isValidUrl(url: string) {
  try {
    new URL(url);
    return true;
  } catch (err) {
    return false;
  }
}

const HyperlinkRow: React.FC<HyperlinkRowProps> = ({
  jobId,
  initialValues,
}) => {
  const saveHyperlinkMutation = useSaveHyperlink();
  const removeHyperlinkMutation = useRemoveHyperlink();
  const [isValid, setIsValid] = React.useState(true);
  const [title, setTitle] = React.useState(initialValues?.title || '');
  const [url, setUrl] = React.useState(initialValues?.url || '');

  const saveHyperlink = async () => {
    if (!isValidUrl(url)) {
      return setIsValid(false);
    }
    setIsValid(true);
    const body: HyperlinkRequest = {
      ...initialValues,
      id: initialValues?.id,
      title,
      url,
      description: '',
      jobId: jobId,
    };

    await saveHyperlinkMutation.mutateAsync({ jobId, body });
    if (!initialValues?.id) {
      setTitle('');
      setUrl('');
    }
  };

  const removeHyperlink = async () => {
    if (!initialValues?.id) return;
    await removeHyperlinkMutation.mutateAsync({
      jobId,
      hyperlinkId: initialValues.id,
    });
  };

  const loading = saveHyperlinkMutation.isPending;

  const hasChanged =
    title !== initialValues?.title || url !== initialValues?.url;

  return (
    <Paper>
      <Stack>
        <Group>
          <TextInput
            label="Display Text"
            placeholder="Enter display text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={{ flex: 1 }}
          />
          <TextInput
            label="URL"
            placeholder="Enter URL"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            style={{ flex: 1 }}
          />
        </Group>
        {url && (
          <Text size="sm" c={isValid ? 'dimmed' : 'red'}>
            {isValid ? (
              <>
                Preview:{' '}
                <a href={url} target="_blank" rel="noreferrer">
                  {title}
                </a>
              </>
            ) : (
              'Invalid URL - http:// or https:// required'
            )}
          </Text>
        )}
        <Group justify="flex-end">
          {initialValues?.id && (
            <DeleteActionIcon
              requiredConfirmation={true}
              confirmationTitle="Remove Hyperlink"
              confirmationText="Are you sure you want to remove this hyperlink?"
              onDelete={removeHyperlink}
            />
          )}
          {initialValues?.id ? (
            <SaveButton
              onClick={saveHyperlink}
              disabled={loading || !hasChanged}
              loading={loading}
            >
              Save
            </SaveButton>
          ) : (
            <CreateButton
              onClick={saveHyperlink}
              disabled={loading || !hasChanged}
              loading={loading}
            >
              Add
            </CreateButton>
          )}
        </Group>
      </Stack>
    </Paper>
  );
};
